/**
 * Central tuning file.
 *
 * Every "magic number" that shapes feel — palette, movement, world size,
 * evolution cadence — lives here so the whole experience can be re-tuned from one
 * place, and so a future editor/console could expose these as live knobs. Nothing
 * in here depends on runtime state.
 */

/* ------------------------------------------------------------------ */
/* Palette — the ink-wash / architectural-model aesthetic             */
/* ------------------------------------------------------------------ */
/**
 * Deliberately desaturated and pale. The city is paper and plaster; colour is
 * used sparingly and mostly emerges through light and the mood system rather than
 * material albedo.
 */
export const PALETTE = {
  sky: 0xe9e7e2,
  fog: 0xe4e1da,
  ground: 0xd8d4cb,
  groundGrid: 0xc4bfb2,
  building: 0xf1efe9,
  buildingShadow: 0xcfcabd,
  road: 0xcbc6bb,
  accent: 0x2b2b2b, // ink — used for edges & rare marks
  glow: 0xfff4e0, // warm window/lantern glow
  // Mood extremes the environment controller interpolates between.
  moodAlive: 0xf3efe6,
  moodBleak: 0xb9b7b1,
  // Set-dressing & atmosphere (the shan-shui layer).
  lantern: 0xffb36b, // warm paper-lantern orange
  pine: 0x4a5248, // garden tree ink-green
  stone: 0xb3ac9e, // garden stone
  water: 0x9fb4ac, // celadon pool
  mountain: 0xb7b3a7, // near ridge ink
  mountainFar: 0xd6d2c8, // far ridge, almost paper
  nightZenith: 0x2e3138, // night sky top — deep ink blue-grey
  nightHorizon: 0x6b6d6e, // night sky at the horizon
  sunGlow: 0xfff0d8, // soft sun/moon disc
} as const;

/* ------------------------------------------------------------------ */
/* World dimensions                                                    */
/* ------------------------------------------------------------------ */
export const WORLD = {
  /** Size of one grid cell in world units. */
  cellSize: 12,
  /** City is GRID x GRID cells; must be even. Grows outward within this bound. */
  grid: 48,
  /** How many cells from centre are initially built. The rest are latent frontier. */
  initialRadius: 7,
  /** Absolute frontier limit (in cells from centre) the city may grow to. */
  maxRadius: 22,
  /** Width of a road relative to cell size. */
  roadWidth: 0.42,
} as const;

/* ------------------------------------------------------------------ */
/* Player movement                                                     */
/* ------------------------------------------------------------------ */
export const PLAYER = {
  eyeHeight: 1.7,
  radius: 0.4,
  // Back to the original, calmer pace — faster felt rushed. Jump height and air
  // control (below) are what make climbing work, not raw speed.
  walkSpeed: 6.5,
  sprintSpeed: 11.5,
  accel: 60,
  airAccel: 34, // strong air control so you can steer onto ledges mid-jump
  friction: 10,
  gravity: 24,
  jumpVelocity: 9.6, // apex ≈ 1.9m — forgiving climbing onto roofs/steps
  /** Mouse look sensitivity (radians per pixel). */
  lookSpeed: 0.0022,
  /** Clamp for vertical look, just under straight up/down. */
  pitchLimit: Math.PI / 2 - 0.05,
  maxStepHeight: 0.8, // auto-step small ledges instead of stalling against them
  /** Grace window (s) to still jump just after walking off an edge. */
  coyoteTime: 0.12,
  /** Window (s) a jump press is remembered so it fires the instant you land. */
  jumpBuffer: 0.12,
} as const;

/* ------------------------------------------------------------------ */
/* AI / evolution cadence                                              */
/* ------------------------------------------------------------------ */
export const AI = {
  /** Seconds between analyzer preference recomputations. */
  analyzeInterval: 0.4,
  /** How quickly the published preference vector chases the raw estimate. Higher =
   *  the city makes up its mind faster, so running a certain way reads sooner. */
  preferenceLambda: 1.3,
  /** Seconds between evolution engine decisions. Frequent enough that the city
   *  feels alive and clearly responds to how you move. */
  evolveInterval: 2.8,
  /** Seconds a single building takes to "grow" into place — quick enough that a
   *  change resolves while you're still looking at it, slow enough to feel calm. */
  growthDuration: 3.4,
  /** Maturity gained per evolution action; the arc completes at 1.0. Together with
   *  the engagement drip, engaged play reaches the finale in roughly 4–5 minutes. */
  maturityPerAction: 0.011,
  /** Extra maturity per second scaled by how actively the player is living in the
   *  city — engaged play is rewarded with faster progress toward the finale. */
  maturityActivityDrip: 0.0015,
  /** Hard ceiling: maturity never falls behind (play time ÷ this), so the ending is
   *  reached within this many seconds of play no matter how the player moves. */
  finaleGuaranteeSeconds: 420,
  /** Speed below which the player is "still" (m/s). */
  stillSpeed: 0.35,
} as const;

/* ------------------------------------------------------------------ */
/* Narrative beats — maturity thresholds that unlock environmental       */
/* shifts and, eventually, words.                                       */
/* ------------------------------------------------------------------ */
export const NARRATIVE_BEATS = [
  { id: 'awakening', at: 0.06, text: undefined },
  { id: 'noticing', at: 0.22, text: 'something here is paying attention.' },
  { id: 'shaping', at: 0.44, text: 'the streets begin to resemble your habits.' },
  { id: 'recognition', at: 0.68, text: 'you have been building this the entire time.' },
  { id: 'mirror', at: 0.9, text: 'this city was never a place.\nit was a mirror.' },
  // The finale beat is wordless: the monolith and the summoning light ARE the
  // message. The ceremony (FinaleDirector) speaks at its own moments — see FINALE.
  { id: 'finale', at: 0.99, text: undefined },
] as const;

/* ------------------------------------------------------------------ */
/* The Remembering — finale ceremony choreography                       */
/* ------------------------------------------------------------------ */
/**
 * Timeline (seconds are phase durations, not absolute):
 * summon → (player within approachRadius of the monolith, or graceSeconds)
 * ascend  riseSeconds       — lift to riseHeight; path ink reveals mid-rise
 * recall  recallSeconds     — city collapses to its seed form…
 *         + holdSeconds     — …and holds there, pale and quiet
 * rebloom rebloomSeconds    — radial regrowth wave from beneath the player
 * descend descendSeconds    — return to ground; epilogue whisper settles in
 */
export const FINALE = {
  approachRadius: 6,
  /** If the player never approaches, the ceremony begins anyway after this. */
  graceSeconds: 45,
  riseHeight: 74,
  riseSeconds: 16,
  /** Path ink starts revealing this many seconds into the ascent. */
  pathRevealAt: 5,
  pathRevealSeconds: 9,
  recallSeconds: 6,
  holdSeconds: 5,
  rebloomSeconds: 9,
  descendSeconds: 10,
  /** How strongly the walked-path ink stays on the ground after the ceremony. */
  pathEpilogueStrength: 0.35,
  /** Radial stagger of the recall/rebloom wave (0 = uniform, 1 = strongly waved). */
  waveAmount: 0.85,
  texts: {
    recall: 'this is the city you arrived in.',
    rebloom: 'and this is everything your walking built.',
    done: 'you may keep walking.\nit will keep listening.',
  },
} as const;

export const DEBUG = {
  /** Toggle the on-screen preference/telemetry HUD with the backtick key. */
  hudDefault: false,
};
